/**
 * Reusable skeleton pieces for ranking / monthly tables.
 * Shared by GHGOverviewPage, StatisticsOverviewPage, and CompanyStatisticsPage
 * to eliminate the repeated inline skeleton-cell JSX that SonarCloud flags as clones.
 */

import React from 'react';
import { Skeleton, TableCell, TableRow } from '@mui/material';

/**
 * A single right-aligned numeric skeleton cell.
 * Kept as a named function so JSX consumers stay concise.
 */
export const NumericSkeletonCell: React.FC = () => (
  <TableCell align="right">
    <Skeleton variant="text" width={70} sx={{ ml: 'auto' }} />
  </TableCell>
);

interface GhgRankingSkeletonRowProps {
  idx: number;
}

/**
 * One skeleton row for the GHG company-rankings table (7 columns).
 */
export const GhgRankingSkeletonRow: React.FC<GhgRankingSkeletonRowProps> = ({ idx }) => (
  <TableRow key={`rank-skeleton-${idx}`}>
    <TableCell><Skeleton variant="rounded" width={32} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
    <TableCell><Skeleton variant="text" width={`${50 + ((idx * 7) % 30)}%`} /></TableCell>
    <NumericSkeletonCell />
    <NumericSkeletonCell />
    <NumericSkeletonCell />
    <NumericSkeletonCell />
    <NumericSkeletonCell />
  </TableRow>
);

interface StatsRankingSkeletonRowProps {
  idx: number;
}

/**
 * One skeleton row for the Statistics company-rankings table (8 columns).
 */
export const StatsRankingSkeletonRow: React.FC<StatsRankingSkeletonRowProps> = ({ idx }) => (
  <TableRow key={`rank-skeleton-${idx}`}>
    <TableCell><Skeleton variant="rounded" width={32} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
    <TableCell><Skeleton variant="text" width={`${50 + ((idx * 7) % 30)}%`} /></TableCell>
    <TableCell><Skeleton variant="rounded" width={48} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
    <NumericSkeletonCell />
    <NumericSkeletonCell />
    <NumericSkeletonCell />
    <TableCell><Skeleton variant="rounded" width={56} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
    <TableCell><Skeleton variant="text" width={90} /></TableCell>
  </TableRow>
);

interface MonthlySkeletonRowProps {
  idx: number;
}

/**
 * One skeleton row for the Statistics monthly-breakdown table (4 columns).
 */
export const MonthlySkeletonRow: React.FC<MonthlySkeletonRowProps> = ({ idx }) => (
  <TableRow key={`monthly-skeleton-${idx}`}>
    <TableCell><Skeleton variant="text" width={90} /></TableCell>
    <NumericSkeletonCell />
    <NumericSkeletonCell />
    <NumericSkeletonCell />
  </TableRow>
);
